import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import workflowScreenSelectors from '../screens/workflows/redux/selectors/workflowScreenSelectors';
import { action } from '../lib/api';
import runLookupAction from '../screens/apps/redux/actions/runLookupAction';
import readLookupList from '../screens/apps/redux/actions/readLookupList';
import parseLookupArguments from './useLookup/parseLookupArguments';
import selectors from '@/redux/selectors/selectors';
import { query } from '@/redux/selectors/querySelectors.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { setLookupLoading } from '@/redux/actions/uiActions/lookupActions';

const useLookup = ({ id, lookupSettings = {} }) => {
  const [forceReload, setForceReload] = useState(false);
  const [parsedArgs, setParsedArgs] = useState({ args: {}, argsChanged: false });
  const isMountedRef = useRef(true);
  const previousArgsRef = useRef(null);

  const app = useSelector(workflowScreenSelectors.activeWorkflowStepApp);
  const stepId = useArgSelector(query, 'stepId');
  const appId = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state)?.id);
  const actionId = useSelector((state) => workflowScreenSelectors.activeWorkflowStepAppAction(state)?.id);
  const credentialId = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.credentialId);
  const stepArgs = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.arguments);
  const isTutorial = useSelector((state) => !!workflowScreenSelectors.activeWorkflowMetadata(state)?.tutorialSlug);
  const stepOutputs = useSelector(selectors.cookedWorkflowStepOutputs);
  const lookupId = `${appId}-${stepId}-${id}`;
  const lookup = useSelector((state) => state?.workflow?.workflowLookups[lookupId]?.data);
  const lookupOptionsFromState = useSelector((state) => state?.workflow?.workflowLookups[lookupId]?.options);
  const lookupArgs = useSelector((state) => state?.workflow?.workflowLookups[lookupId]?.args);
  const loading = useSelector((state) => state?.ui?.lookupLoading)?.[lookupId];

  const lookupLabel = lookupSettings?.lookupLabel;
  const lookupValue = lookupSettings?.lookupValue;
  const lookupProviderToken = lookupSettings?.lookupProviderToken;
  const lookupActionId = lookupSettings?.lookupActionId;
  const lookupOptions = lookupSettings?.actionInstance?.options;
  const listType = lookupSettings?.type;
  const isFixed = listType === 'fixed' || !listType;
  const isPreset = !['fixed', 'lookup'].includes(listType);

  const options =
    lookupSettings?.fields?.map((n) => ({ label: n.label, value: n.key })) ||
    Object.entries(lookupSettings?.fixedOptions || {})
      .map(([key, value]) => {
        if (typeof value === 'object' && value !== null && 'label' in value) {
          return { label: value.label, value: key, sequence: typeof value.sequence === 'number' ? value.sequence : Number.MAX_SAFE_INTEGER };
        }
        return { label: value, value: key, sequence: Number.MAX_SAFE_INTEGER };
      })
      .sort((a, b) => (a.sequence || Number.MAX_SAFE_INTEGER) - (b.sequence || Number.MAX_SAFE_INTEGER));

  // Parse arguments asynchronously
  useEffect(() => {
    const parseArgs = async () => {
      try {
        const result = await parseLookupArguments({
          args: lookupSettings?.actionInstance?.arguments,
          stepArgs,
          previousArgs: lookupArgs,
          actionId,
          app,
          responses: stepOutputs
        });
        if (isMountedRef.current) {
          setParsedArgs(result);
        }
      } catch (error) {
        console.error('Error parsing lookup arguments:', error);
        if (isMountedRef.current) {
          setParsedArgs({ args: {}, argsChanged: false });
        }
      }
    };

    parseArgs();
  }, [lookupSettings?.actionInstance?.arguments, stepArgs, lookupArgs, actionId, app, stepOutputs]);

  const { args, argsChanged } = parsedArgs;

  const formattedLookup = useMemo(() => {
    if (isFixed) {
      return options;
    }
    if (Array.isArray(lookupOptionsFromState)) {
      return lookupOptionsFromState;
    }
    if (Array.isArray(lookup)) {
      return isPreset
        ? lookup
        : lookup?.map((item) => ({
            label: item[lookupLabel],
            value: item[lookupValue],
            required: item.required === 'true' || item.required === true,
            ...(lookupProviderToken && item?.[lookupProviderToken] != null && item?.[lookupProviderToken] !== '' ? { providerToken: item[lookupProviderToken] } : {})
          }));
    }
    return [];
  }, [isFixed, isPreset, lookup, lookupOptionsFromState, options, lookupLabel, lookupValue, lookupProviderToken]);

  const reloadFixedList = useCallback(
    (listType, opts = {}) => {
      setLookupLoading(lookupId, true);
      readLookupList({
        lookupId,
        listType,
        force: opts.force,
        callback: (data) => {
          if (data) {
            action('SET_WORKFLOW_LOOKUP', { id: lookupId, data });
          }
          setLookupLoading(lookupId, false);
        }
      });
    },
    [lookupId]
  );

  const lookupReloadQueue = useSelector((state) => state?.workflow?.lookupReloadQueue || []);

  const reload = useCallback(
    ({ cache, force = false, customArgs }) => {
      if (lookup !== undefined && !force) {
        return;
      }
      if (!['lookup', 'fixed'].includes(listType) && listType) {
        reloadFixedList(listType, { force });
        return;
      }
      if (!lookupActionId) {
        return;
      }
      if (listType === 'fixed') {
        return;
      }
      setLookupLoading(lookupId, true);

      const lookupSettingsForApi =
        lookupSettings?.useCustomLabelCode && lookupSettings?.lookupLabelMapper
          ? {
              lookupValue: lookupSettings.lookupValue,
              lookupLabel: lookupSettings.lookupLabel,
              lookupProviderToken: lookupSettings.lookupProviderToken,
              useCustomLabelCode: true,
              lookupLabelMapper: lookupSettings.lookupLabelMapper
            }
          : undefined;
      runLookupAction({
        lookupId,
        appId,
        appVersion: app?.version,
        actionId: lookupActionId,
        arguments: customArgs || args,
        options: lookupOptions,
        stepId,
        credentialId,
        lookup: true,
        cache,
        force,
        isTutorial,
        lookupSettings: lookupSettingsForApi,
        callback: (data) => {
          if (data) {
            const rows = Array.isArray(data) ? data : data?.rows;
            const optionsFromApi = data?.options;
            action('SET_WORKFLOW_LOOKUP', { id: lookupId, data: rows, options: optionsFromApi, args });
          }
          setLookupLoading(lookupId, false);
        }
      });
    },
    [lookup, lookupId, lookupActionId, appId, app?.version, args, stepId, credentialId, listType, isTutorial, reloadFixedList, lookupSettings]
  );

  useEffect(() => {
    if (previousArgsRef.current === null) {
      previousArgsRef.current = args;
      return;
    }

    const previousArgsString = JSON.stringify(previousArgsRef.current);
    const currentArgsString = JSON.stringify(args);

    if (previousArgsString !== currentArgsString) {
      previousArgsRef.current = args;
      reload({ cache: false, force: true });
    }
  }, [args, reload]);

  useEffect(() => {
    if (forceReload) {
      reload({ cache: false, force: true });
      setForceReload(false);
    } else {
      reload({ cache: true });
    }
  }, [lookupActionId, listType, reload, forceReload]);

  useEffect(() => {
    if (argsChanged) {
      setForceReload(true);
      setLookupLoading(lookupId, true);
    }
  }, [argsChanged, lookupId]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (lookupReloadQueue.includes(id)) {
      setLookupLoading(lookupId, true);
      action('SET_WORKFLOW_LOOKUP', { id: lookupId, data: [] });

      reload({ cache: false, force: true, customArgs: stepArgs });

      action('REMOVE_LOOKUP_RELOAD_QUEUE', { id });
    }
  }, [lookupReloadQueue, id]);

  return {
    lookup: formattedLookup,
    loading,
    reload: () => reload({ cache: false, force: true })
  };
};

export default useLookup;
